const DISCIPLINE_KEY_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

class RealLessonDisciplines extends HTMLElement {
  connectedCallback() {
    if (this.initializing) return;

    this.initializing = true;
    this.isDesignMode =
      this.hasAttribute('data-real-lesson-design-mode') ||
      window.Shopify?.designMode === true;
    this.selectedDiscipline = null;
    this.expanded = true;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mobileViewport = window.matchMedia('(max-width: 749px)');
    this.lessonLandingFrame = null;
    this.lessonLandingTimer = null;

    this.onChange = this.onChange.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onProgramsDisciplineChange = this.onProgramsDisciplineChange.bind(this);
    this.onShopifyBlockSelect = this.onShopifyBlockSelect.bind(this);
    this.onShopifySectionLoad = this.onShopifySectionLoad.bind(this);
    this.onMobileViewportChange = this.onMobileViewportChange.bind(this);

    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeyDown);
    this.addEventListener('pointerdown', this.onPointerDown);
    document.addEventListener('shopify:block:select', this.onShopifyBlockSelect);
    document.addEventListener('shopify:section:load', this.onShopifySectionLoad);
    this.mobileViewport.addEventListener('change', this.onMobileViewportChange);

    this.validateDisciplineCards();
    this.connectToPrograms();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeyDown);
    this.removeEventListener('pointerdown', this.onPointerDown);
    document.removeEventListener('shopify:block:select', this.onShopifyBlockSelect);
    document.removeEventListener('shopify:section:load', this.onShopifySectionLoad);
    this.mobileViewport?.removeEventListener('change', this.onMobileViewportChange);
    this.programs?.removeEventListener(
      'real:lesson-discipline-change',
      this.onProgramsDisciplineChange
    );
    this.targetObserver?.disconnect();
    this.targetObserver = null;
    window.clearTimeout(this.choiceActivationTimer);
    this.cancelLessonLanding();
    this.initializing = false;
  }

  async connectToPrograms() {
    await customElements.whenDefined('real-lesson-programs');
    if (!this.isConnected) return;

    if (this.attachPrograms()) return;

    this.targetObserver?.disconnect();
    this.targetObserver = new MutationObserver(() => {
      if (this.attachPrograms()) {
        this.targetObserver?.disconnect();
        this.targetObserver = null;
      }
    });
    this.targetObserver.observe(document.body, { childList: true, subtree: true });
  }

  attachPrograms() {
    const targetId = this.dataset.lessonSectionAnchor;
    const programs = targetId ? document.getElementById(targetId) : null;

    if (
      !(programs instanceof HTMLElement) ||
      programs.localName !== 'real-lesson-programs' ||
      typeof programs.filterByDiscipline !== 'function' ||
      typeof programs.clearDiscipline !== 'function' ||
      typeof programs.getDisciplineCount !== 'function'
    ) {
      return false;
    }

    if (this.programs !== programs) {
      this.programs?.removeEventListener(
        'real:lesson-discipline-change',
        this.onProgramsDisciplineChange
      );
      this.programs = programs;
      this.programs.addEventListener(
        'real:lesson-discipline-change',
        this.onProgramsDisciplineChange
      );
    }

    this.validateDisciplineCards();
    this.syncDisciplineMedia();
    const activeDiscipline = programs.selectedDiscipline;
    this.selectedDiscipline = this.hasDiscipline(activeDiscipline)
      ? activeDiscipline
      : null;

    if (activeDiscipline && !this.selectedDiscipline) {
      this.isUpdatingPrograms = true;
      try {
        programs.clearDiscipline();
      } finally {
        this.isUpdatingPrograms = false;
      }
    }

    this.expanded = !this.selectedDiscipline;
    this.updateCounts();
    this.updateEditorDiagnostics();
    this.updateState({ announce: false });
    this.hidden = false;
    return true;
  }

  onShopifySectionLoad() {
    this.validateDisciplineCards();

    if (!this.programs?.isConnected) {
      this.hidden = true;
      this.connectToPrograms();
      return;
    }

    this.syncDisciplineMedia();

    if (
      this.selectedDiscipline &&
      !this.hasDiscipline(this.selectedDiscipline)
    ) {
      this.clearDiscipline({ announce: false, focus: false });
    }

    this.updateCounts();
    this.updateEditorDiagnostics();
    this.updateState({ announce: false });
  }

  onMobileViewportChange(event) {
    if (!event.matches) this.cancelLessonLanding();
  }

  onShopifyBlockSelect(event) {
    const blockId = event.detail?.blockId;
    if (!blockId) return;

    const block = this.querySelector(
      `[data-real-lesson-discipline-block-id="${CSS.escape(blockId)}"]`
    );
    if (!block) return;

    const input = block.querySelector('[data-real-lesson-discipline-choice]');
    if (!input) return;

    if (input.disabled || !this.hasDiscipline(input.value)) {
      this.expanded = true;
      this.updateState({ announce: false });
    } else {
      this.applyDiscipline(input.value, { collapse: false, announce: false });
    }

    block.scrollIntoView({
      behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
      block: 'center',
    });
  }

  onProgramsDisciplineChange(event) {
    if (this.isUpdatingPrograms) return;
    if (event.currentTarget !== this.programs) return;
    if (
      event.detail?.programsId &&
      event.detail.programsId !== this.programs.id
    ) {
      return;
    }

    const disciplineKey = event.detail?.disciplineKey || null;
    const input = disciplineKey
      ? this.authoritativeDisciplineCards
          ?.get(disciplineKey)
          ?.querySelector('[data-real-lesson-discipline-choice]')
      : null;

    this.selectedDiscipline = input ? disciplineKey : null;
    this.expanded = !this.selectedDiscipline;
    this.updateCounts();
    this.updateState();
  }

  onChange(event) {
    const input = event.target.closest?.('[data-real-lesson-discipline-choice]');
    if (!input || !this.contains(input)) return;
    if (input.value === this.selectedDiscipline) return;

    // Native radio arrow-key navigation fires change without click. Keep the
    // choices open so keyboard users can review each selection.
    this.applyDiscipline(input.value, {
      collapse: this.activatingChoice === input,
      focusSummary: input.matches(':focus-visible'),
    });
  }

  onClick(event) {
    const input = event.target.closest?.('[data-real-lesson-discipline-choice]');

    if (input && this.contains(input)) {
      // Pointer activation and Space both fire click on a native radio.
      const focusSummary = input.matches(':focus-visible');
      this.markChoiceActivation(input);

      if (input.value !== this.selectedDiscipline) {
        this.applyDiscipline(input.value, {
          collapse: true,
          focusSummary,
        });
      } else if (this.expanded) {
        this.expanded = false;
        this.updateState();
        if (!this.isDesignMode && this.mobileViewport.matches) {
          this.scheduleLessonLanding();
        }
      }

      if (focusSummary || this.isDesignMode || !this.mobileViewport.matches) {
        window.requestAnimationFrame(() => {
          this.querySelector('[data-real-lesson-discipline-clear]')?.focus({
            preventScroll: true,
          });
        });
      }
      return;
    }

    const pill = event.target.closest?.('[data-real-lesson-discipline-pill]');

    if (pill && this.contains(pill)) {
      this.applyDiscipline(pill.dataset.realLessonDisciplinePill, {
        collapse: true,
      });
      return;
    }

    const clear = event.target.closest?.('[data-real-lesson-discipline-clear]');

    if (clear && this.contains(clear)) {
      // Keep the reset focus path for keyboard and assistive-technology
      // activation, but do not leave a pointer user focused on the first card.
      this.clearDiscipline({ focus: event.detail === 0 });
    }
  }

  onPointerDown(event) {
    const input = event.target.closest?.('[data-real-lesson-discipline-choice]');
    if (!input || !this.contains(input)) return;

    this.markChoiceActivation(input);
  }

  onKeyDown(event) {
    const input = event.target.closest?.('[data-real-lesson-discipline-choice]');
    if (!input || !this.contains(input) || event.key !== ' ') return;

    this.markChoiceActivation(input);
  }

  markChoiceActivation(input) {
    this.activatingChoice = input;
    window.clearTimeout(this.choiceActivationTimer);
    this.choiceActivationTimer = window.setTimeout(() => {
      this.activatingChoice = null;
      this.choiceActivationTimer = null;
    }, 0);
  }

  applyDiscipline(
    disciplineKey,
    { collapse = true, announce = true, focusSummary = false } = {}
  ) {
    if (!this.programs || !this.hasDiscipline(disciplineKey)) return;

    const disciplineChanged = this.selectedDiscipline !== disciplineKey;
    const shouldLandOnLessons =
      collapse &&
      !this.isDesignMode &&
      this.mobileViewport.matches &&
      this.expanded;

    if (disciplineChanged && !this.expanded) this.cancelLessonLanding();

    this.selectedDiscipline = disciplineKey;
    this.expanded = !collapse;
    this.isUpdatingPrograms = true;
    try {
      this.programs.filterByDiscipline(disciplineKey);
    } finally {
      this.isUpdatingPrograms = false;
    }
    this.updateCounts();
    this.updateState({ announce });

    if (shouldLandOnLessons) {
      this.scheduleLessonLanding();
    }

    if (focusSummary) {
      window.requestAnimationFrame(() => {
        this.querySelector('[data-real-lesson-discipline-clear]')?.focus({
          preventScroll: true,
        });
      });
    }
  }

  clearDiscipline({ announce = true, focus = true } = {}) {
    if (!this.programs) return;

    this.cancelLessonLanding();
    this.selectedDiscipline = null;
    this.expanded = true;
    this.isUpdatingPrograms = true;
    try {
      this.programs.clearDiscipline();
    } finally {
      this.isUpdatingPrograms = false;
    }
    this.updateCounts();
    this.updateState({ announce });

    if (focus) {
      window.requestAnimationFrame(() => {
        this.querySelector(
          '[data-real-lesson-discipline-choice]:not(:disabled)'
        )?.focus({ preventScroll: true });
      });
    }
  }

  hasDiscipline(disciplineKey) {
    return Boolean(
      disciplineKey && this.activeDisciplineKeys?.has(disciplineKey)
    );
  }

  scheduleLessonLanding() {
    this.cancelLessonLanding();

    const settleDelay = this.reducedMotion.matches ? 0 : 430;
    this.lessonLandingTimer = window.setTimeout(() => {
      this.lessonLandingTimer = null;
      this.lessonLandingFrame = window.requestAnimationFrame(() => {
        this.lessonLandingFrame = null;
        this.landOnLessons();
      });
    }, settleDelay);

    this.cancelPendingLessonLanding = () => this.cancelLessonLanding();
    document.addEventListener('pointerdown', this.cancelPendingLessonLanding, {
      capture: true,
      passive: true,
      once: true,
    });
    document.addEventListener('wheel', this.cancelPendingLessonLanding, {
      capture: true,
      passive: true,
      once: true,
    });
    document.addEventListener('keydown', this.cancelPendingLessonLanding, {
      capture: true,
      once: true,
    });
  }

  cancelLessonLanding() {
    window.clearTimeout(this.lessonLandingTimer);
    this.lessonLandingTimer = null;

    if (this.lessonLandingFrame) {
      window.cancelAnimationFrame(this.lessonLandingFrame);
      this.lessonLandingFrame = null;
    }

    this.removeLessonLandingCancellation();
  }

  removeLessonLandingCancellation() {
    if (!this.cancelPendingLessonLanding) return;

    document.removeEventListener(
      'pointerdown',
      this.cancelPendingLessonLanding,
      { capture: true }
    );
    document.removeEventListener(
      'wheel',
      this.cancelPendingLessonLanding,
      { capture: true }
    );
    document.removeEventListener(
      'keydown',
      this.cancelPendingLessonLanding,
      { capture: true }
    );
    this.cancelPendingLessonLanding = null;
  }

  landOnLessons() {
    this.removeLessonLandingCancellation();

    if (!this.isConnected || !this.mobileViewport.matches || this.expanded) return;

    const target = this.querySelector('[data-real-lesson-discipline-collapsed]');
    if (!target) return;

    target.scrollIntoView({
      behavior: this.reducedMotion.matches ? 'instant' : 'smooth',
      block: 'start',
    });
  }

  validateDisciplineCards() {
    const seenKeys = new Set();
    const activeKeys = new Set();
    const authoritativeCards = new Map();
    const invalidCards = [];

    this.querySelectorAll('[data-real-lesson-discipline-card]').forEach((card) => {
      const input = card.querySelector('[data-real-lesson-discipline-choice]');
      if (!input) return;

      const disciplineKey = String(input.value || '').trim();
      const warnings = [];
      let isAuthoritative = false;

      input.value = disciplineKey;

      if (!disciplineKey) {
        warnings.push(
          'Discipline key is required. Add a lowercase key such as wing-foil.'
        );
      } else if (!DISCIPLINE_KEY_PATTERN.test(disciplineKey)) {
        warnings.push(
          `“${disciplineKey}” is invalid. Use lowercase letters, numbers, and single hyphens only.`
        );
      } else if (seenKeys.has(disciplineKey)) {
        warnings.push(
          `“${disciplineKey}” duplicates an earlier Discipline block. The first block is used.`
        );
      } else {
        seenKeys.add(disciplineKey);
        activeKeys.add(disciplineKey);
        authoritativeCards.set(disciplineKey, card);
        isAuthoritative = true;
      }

      card.realLessonDisciplineWarnings = warnings;
      card.toggleAttribute(
        'data-real-lesson-discipline-active',
        isAuthoritative
      );
      card.hidden = !isAuthoritative && !this.isDesignMode;
      input.disabled = !isAuthoritative;
      input.setAttribute('aria-invalid', isAuthoritative ? 'false' : 'true');

      const count = card.querySelector('[data-real-lesson-discipline-count]');
      if (count) {
        count.dataset.realLessonDisciplineCount = disciplineKey;
        if (!isAuthoritative) {
          count.textContent = '';
          delete count.dataset.count;
        }
      }

      const blockId = card.dataset.realLessonDisciplineBlockId;
      const pill = blockId
        ? this.querySelector(
            `[data-real-lesson-discipline-pill-block-id="${CSS.escape(blockId)}"]`
          )
        : null;

      if (pill) {
        pill.dataset.realLessonDisciplinePill = disciplineKey;
        pill.hidden = !isAuthoritative;
        pill.disabled = !isAuthoritative;
      }

      if (!isAuthoritative) invalidCards.push(card);
      this.renderCardWarning(card);
    });

    this.activeDisciplineKeys = activeKeys;
    this.authoritativeDisciplineCards = authoritativeCards;
    this.invalidDisciplineCards = invalidCards;
    return activeKeys;
  }

  syncDisciplineMedia() {
    if (typeof this.programs?.setDisciplineMedia !== 'function') return;

    const disciplineMedia = new Map();

    this.authoritativeDisciplineCards?.forEach((card, disciplineKey) => {
      const image = card.querySelector('[data-real-lesson-discipline-media] img');
      if (!image) return;

      disciplineMedia.set(disciplineKey, {
        key: disciplineKey,
        image,
        position:
          card.dataset.realLessonDisciplineImagePosition || 'center center',
      });
    });

    this.programs.setDisciplineMedia(disciplineMedia);
  }

  renderCardWarning(card, extraWarnings = []) {
    const warning = card.querySelector('[data-real-lesson-discipline-warning]');
    const warnings = [
      ...(card.realLessonDisciplineWarnings || []),
      ...extraWarnings,
    ];
    const showWarning = this.isDesignMode && warnings.length > 0;

    card.classList.toggle('has-discipline-warning', showWarning);
    if (!warning) return;

    warning.textContent = warnings.join(' ');
    warning.hidden = !showWarning;
  }

  updateEditorDiagnostics() {
    if (!this.isDesignMode || !this.programs?.isConnected) return;

    const activeKeys = new Set(this.activeDisciplineKeys || []);
    let programsResult;

    try {
      programsResult = this.programs.updateDisciplineWarnings?.(
        new Set(activeKeys)
      );
    } catch (error) {
      console.warn('Unable to update lesson discipline warnings.', error);
    }

    const unknownKeys = [
      ...new Set(
        (Array.isArray(programsResult?.unknownKeys)
          ? programsResult.unknownKeys
          : []
        )
          .filter((key) => typeof key === 'string')
          .map((key) => key.trim())
          .filter(Boolean)
      ),
    ];
    const zeroResultKeys = [...activeKeys].filter(
      (disciplineKey) =>
        (Number(this.programs.getDisciplineCount(disciplineKey)) || 0) === 0
    );
    const zeroResultKeySet = new Set(zeroResultKeys);

    this.authoritativeDisciplineCards?.forEach((card, disciplineKey) => {
      const warnings = zeroResultKeySet.has(disciplineKey)
        ? [`No lesson cards currently map to “${disciplineKey}”.`]
        : [];
      this.renderCardWarning(card, warnings);
    });

    const diagnostics = this.querySelector(
      '[data-real-lesson-discipline-diagnostics]'
    );
    const list = diagnostics?.querySelector(
      '[data-real-lesson-discipline-diagnostic-list]'
    );
    if (!diagnostics || !list) return;

    const messages = [];

    if (this.invalidDisciplineCards?.length) {
      const noun =
        this.invalidDisciplineCards.length === 1 ? 'block is' : 'blocks are';
      messages.push(
        `${this.invalidDisciplineCards.length} Discipline ${noun} excluded from storefront filtering. Fix the highlighted card${
          this.invalidDisciplineCards.length === 1 ? '' : 's'
        }.`
      );
    }

    if (activeKeys.size === 0) {
      messages.push('No valid, unique discipline keys are configured.');
    }

    if (unknownKeys.length > 0) {
      messages.push(
        `Lesson cards reference unknown discipline keys: ${unknownKeys.join(', ')}.`
      );
    }

    if (zeroResultKeys.length > 0) {
      messages.push(
        `Valid disciplines with no matching lessons: ${zeroResultKeys.join(', ')}.`
      );
    }

    list.replaceChildren(
      ...messages.map((message) => {
        const item = document.createElement('li');
        item.textContent = message;
        return item;
      })
    );
    diagnostics.hidden = messages.length === 0;
  }

  updateCounts() {
    if (!this.programs) return;

    this.querySelectorAll('[data-real-lesson-discipline-count]').forEach((count) => {
      const disciplineKey = count.dataset.realLessonDisciplineCount;

      if (!this.hasDiscipline(disciplineKey)) {
        count.textContent = '';
        delete count.dataset.count;
        return;
      }

      const lessonCount = Number(this.programs.getDisciplineCount(disciplineKey)) || 0;
      count.textContent = this.formatCount(lessonCount);
      count.dataset.count = String(lessonCount);
    });
  }

  updateState({ announce = true } = {}) {
    const expanded = this.querySelector('[data-real-lesson-discipline-expanded]');
    const collapsed = this.querySelector('[data-real-lesson-discipline-collapsed]');
    const status = this.querySelector('[data-real-lesson-discipline-status]');
    const clear = this.querySelector('[data-real-lesson-discipline-clear]');
    const showExpanded = !this.selectedDiscipline || this.expanded;

    if (expanded) expanded.hidden = !showExpanded;
    if (collapsed) collapsed.hidden = showExpanded;
    if (clear) clear.setAttribute('aria-expanded', showExpanded ? 'true' : 'false');

    this.querySelectorAll('[data-real-lesson-discipline-choice]').forEach((input) => {
      input.checked =
        !input.disabled && input.value === this.selectedDiscipline;
    });

    let activePill = null;
    this.querySelectorAll('[data-real-lesson-discipline-pill]').forEach((pill) => {
      const isActive =
        !pill.disabled &&
        pill.dataset.realLessonDisciplinePill === this.selectedDiscipline;
      pill.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      if (isActive) activePill = pill;
    });

    if (
      activePill &&
      !showExpanded &&
      this.hasAttribute('data-real-discipline-picker-enhanced') &&
      window.matchMedia('(max-width: 749px)').matches
    ) {
      window.requestAnimationFrame(() => {
        if (!activePill.isConnected) return;
        const pillScroller = activePill.parentElement;
        if (!pillScroller) return;

        const scrollerRect = pillScroller.getBoundingClientRect();
        const pillRect = activePill.getBoundingClientRect();
        const centeredLeft =
          pillScroller.scrollLeft +
          pillRect.left -
          scrollerRect.left -
          (pillScroller.clientWidth - pillRect.width) / 2;

        pillScroller.scrollTo({
          left: Math.max(0, centeredLeft),
          behavior: this.reducedMotion.matches ? 'instant' : 'smooth',
        });
      });
    }

    const selectedCard = this.selectedDiscipline
      ? this.authoritativeDisciplineCards?.get(this.selectedDiscipline)
      : null;
    const selectedTitle =
      selectedCard?.dataset.realLessonDisciplineTitle || this.selectedDiscipline;
    const resultCount = this.selectedDiscipline
      ? Number(this.programs?.getDisciplineCount(this.selectedDiscipline)) || 0
      : null;
    const selectionText = this.querySelector(
      '[data-real-lesson-discipline-selection-text]'
    );

    if (selectionText) {
      const selectedPrefix = this.dataset.selectedPrefix || 'Filtered to';
      selectionText.textContent = this.selectedDiscipline
        ? `${selectedPrefix} ${selectedTitle}`
        : '';
    }

    if (!status) return;

    status.setAttribute('aria-live', announce ? 'polite' : 'off');
    status.textContent = this.selectedDiscipline
      ? `${selectedTitle} selected. Showing ${this.formatCount(resultCount)}.`
      : '';

    if (!announce) {
      window.requestAnimationFrame(() => {
        if (status.isConnected) status.setAttribute('aria-live', 'polite');
      });
    }
  }

  formatCount(count) {
    const noun =
      count === 1
        ? this.dataset.lessonSingular || 'lesson'
        : this.dataset.lessonPlural || 'lessons';

    return `${count} ${noun}`;
  }
}

if (!customElements.get('real-lesson-disciplines')) {
  customElements.define('real-lesson-disciplines', RealLessonDisciplines);
}
